/**
 * DocumentsSubTab.tsx
 *
 * Combined documents list matching legacy quest-lab-list.php DOCUMENTS tab.
 * Shows note-based labs + Quest PDFs + Vibrant PDFs + Access Labs results
 * (which open the Access result view, with their status shown as a badge).
 * Columns: Collect Date, Result Date, Title, Source. Rows are clickable to
 * preview the PDF (or download non-PDF attachments).
 * Includes search, source filter, and date range.
 *
 * Used for two sub-tabs: the "All" tab (everything, uploadsOnly=false) and the
 * "Documents" tab (uploaded documents only, uploadsOnly=true) which hides the
 * API-sourced Quest/Vibrant results.
 */
import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2, RotateCcw, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { getPatientLabDocuments, deleteLabDocument, restoreLabDocument, downloadNoteAttachment, fetchLabAttachmentDebug, type LabResult, type LabDebugResult, type LabDocument } from '@/services/labs.service';
import { Button, ConfirmDialog, Spinner } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import type { LabPreviewTarget } from './LabPdfPreviewPanel';
import { AccessResultStatusBadge } from './AccessResultStatusBadge';
import { parseSearchQuery, textMatchesTokens } from './labSearchUtils';
import { DatePicker } from '@/components/ui/DatePicker';

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? value : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

interface CombinedDoc {
  key: string;
  /** Result Date. */
  date: string | null;
  /** Collect Date (specimen collection date), when available. */
  collectDate: string | null;
  title: string;
  source: string;
  downloadSource?: string;
  downloadId?: number;
  noteId?: number;
  /** The note behind this row is soft-deleted (notes.active = 0). */
  deleted?: boolean;
  /** Who deleted it, when we have a record of it. */
  deletedBy?: string | null;
  deletedAt?: string | null;
  attachment?: { attachmentId: number; filename: string };
  /** Access Labs rows: open the Access result view, with the status shown. */
  access?: { labResultId: number; previewTitle: string; status: string | null; statusLabel: string | null };
}

type SortKey = 'collectDate' | 'date' | 'title' | 'source';
type SortDir = 'asc' | 'desc';

/**
 * "(deleted)" on its own, or with the attribution when we have it. Anything
 * deleted before deletions started being recorded has no name to show, and a
 * half-built string ("deleted by ", a stray separator) is worse than none.
 */
function deletedLabel(doc: { deletedBy?: string | null; deletedAt?: string | null }): string {
  const who = doc.deletedBy?.trim();
  if (!who) return '(deleted)';
  const when = doc.deletedAt ? formatDate(doc.deletedAt) : null;
  return when && when !== '—' ? `(deleted by ${who} · ${when})` : `(deleted by ${who})`;
}

function isPdfFilename(filename: string | undefined | null): boolean {
  return !!filename && filename.toLowerCase().endsWith('.pdf');
}

interface DocumentsSubTabProps {
  patientId: number;
  results: LabResult[];
  resultsLoading: boolean;
  onPreview: (target: LabPreviewTarget) => void;
  /**
   * When true, show only uploaded documents (note-based labs) and hide the
   * API-sourced Quest/Vibrant results. Powers the "Documents" tab; the "All"
   * tab leaves this false to show everything.
   */
  uploadsOnly?: boolean;
  /**
   * Source for the note-based document list. Defaults to the Labs list
   * (getPatientLabDocuments); the Imaging/Genetics sections pass their own
   * fetcher + a distinct query key so the three lists cache independently.
   */
  documentsFetcher?: (patientId: number, includeDeleted: boolean) => Promise<LabDocument[]>;
  documentsQueryKey?: string;
  /**
   * Offer the "Show deleted" checkbox. Opt-in per section: the Labs "All" tab
   * leaves it off. Holders of "Delete Labs" (permission 23) see it -- the same
   * grant as the delete button, and the same one the API enforces.
   */
  allowShowDeleted?: boolean;
  /** Empty-state message (varies per section). */
  emptyMessage?: string;
}

export function DocumentsSubTab({
  patientId,
  results,
  resultsLoading,
  onPreview,
  uploadsOnly = false,
  documentsFetcher = getPatientLabDocuments,
  documentsQueryKey = 'patient-lab-documents',
  emptyMessage = 'No lab documents found.',
  allowShowDeleted = false,
}: DocumentsSubTabProps) {
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<CombinedDoc | null>(null);
  const [sortBy, setSortBy] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [showDeleted, setShowDeleted] = useState(false);

  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const canDeleteLabs = hasPermission(23);
  const canShowDeleted = allowShowDeleted && canDeleteLabs;
  const includeDeleted = canShowDeleted && showDeleted;
  // Debug tooling is disabled for all user levels.
  const showDebug = false;

  const [debugTitle, setDebugTitle] = useState<string | null>(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugResult, setDebugResult] = useState<LabDebugResult | null>(null);
  const [debugError, setDebugError] = useState<string | null>(null);

  async function runDebug(pid: number, noteId: number, attachmentId: number, filename: string) {
    setDebugTitle(filename || `attachment ${attachmentId}`);
    setDebugResult(null);
    setDebugError(null);
    setDebugLoading(true);
    try {
      const res = await fetchLabAttachmentDebug(pid, noteId, attachmentId);
      setDebugResult(res);
    } catch (e) {
      setDebugError(e instanceof Error ? e.message : 'Debug request failed');
    } finally {
      setDebugLoading(false);
    }
  }

  const { data: noteDocs, isLoading: docsLoading } = useQuery({
    // includeDeleted belongs in the key so toggling refetches. The existing
    // invalidateQueries([documentsQueryKey, patientId]) callers still match --
    // React Query matches key prefixes by default.
    queryKey: [documentsQueryKey, patientId, includeDeleted],
    queryFn: () => documentsFetcher(patientId, includeDeleted),
    staleTime: 60_000,
  });

  const combinedDocs = useMemo<CombinedDoc[]>(() => {
    const docs: CombinedDoc[] = [];

    for (const doc of noteDocs ?? []) {
      for (const att of doc.attachments) {
        const displayTitle = att.filename ? `${doc.title} (${att.filename})` : doc.title;
        docs.push({
          key: `note-${doc.noteId}-att-${att.attachmentId}`,
          date: doc.date,
          collectDate: doc.collectionDate ?? null,
          title: displayTitle,
          source: doc.source,
          noteId: doc.noteId,
          deleted: doc.deleted,
          deletedBy: doc.deletedBy,
          deletedAt: doc.deletedAt,
          attachment: att,
        });
      }
    }

    // The "Documents" tab lists only uploaded documents; skip the API-sourced
    // Quest/Vibrant results that the "All" tab includes.
    if (!uploadsOnly) for (const r of results) {
      if (r.source === 'quest') {
        if (!r.hasPdf) continue;
        docs.push({
          key: `quest-${r.labResultId}`,
          // Result Date = HL7 reported date (OBR-22), else the ingest date.
          date: r.resultedAt ?? r.orderedAt,
          // Collect Date = HL7 specimen collection date (OBR-7), else the ingest date.
          collectDate: r.collectedAt ?? r.orderedAt,
          title: r.orderNumber
            ? `Quest Diagnostics Report (#${r.orderNumber})`
            : 'Quest Diagnostics Report',
          source: 'Quest Diagnostics (API)',
          downloadSource: 'quest',
          downloadId: r.labResultId,
        });
      } else if (r.source === 'vibrant') {
        docs.push({
          key: `vibrant-${r.labResultId}`,
          // Result Date = HL7 reported date (OBR-22), else the ingest date.
          date: r.resultedAt ?? r.orderedAt,
          // Collect Date = HL7 specimen collection date (OBR-7), else the ingest date.
          collectDate: r.collectedAt ?? r.orderedAt,
          title: r.testsSummary ?? 'Vibrant Wellness Result',
          source: 'Vibrant Wellness',
          downloadSource: 'vibrant',
          downloadId: r.labResultId,
        });
      } else if (r.source === 'access') {
        // Every Access result opens its values view, PDF or not.
        docs.push({
          key: r.rowKey,
          date: r.resultedAt ?? r.orderedAt,
          collectDate: r.collectedAt ?? r.orderedAt,
          title: r.testsSummary ?? 'Access Labs Result',
          source: r.sourceName ?? 'Access Labs',
          access: {
            labResultId: r.labResultId,
            previewTitle: r.orderNumber ? `Access Labs Accession #${r.orderNumber}` : 'Access Labs Result',
            status: r.resultStatus ?? null,
            statusLabel: r.orcStatus,
          },
        });
      }
    }

    return docs;
  }, [noteDocs, results, uploadsOnly]);

  const sources = useMemo(() => {
    const set = new Set<string>();
    for (const d of combinedDocs) if (d.source) set.add(d.source);
    return Array.from(set).sort();
  }, [combinedDocs]);

  const filtered = useMemo(() => {
    let rows = combinedDocs;

    if (search.trim()) {
      const { includeTokens, excludeTokens } = parseSearchQuery(search);
      rows = rows.filter((d) => textMatchesTokens(d.title + ' ' + d.source, includeTokens, excludeTokens));
    }

    if (sourceFilter) {
      rows = rows.filter((d) => d.source === sourceFilter);
    }

    if (dateFrom) {
      rows = rows.filter((d) => (d.date ?? '') >= dateFrom);
    }
    if (dateTo) {
      rows = rows.filter((d) => (d.date ?? '') <= dateTo + 'T23:59:59');
    }

    const sorted = [...rows].sort((a, b) => {
      const av = (a[sortBy] ?? '').toString();
      const bv = (b[sortBy] ?? '').toString();
      // Empty values always sort last regardless of direction.
      if (av === '' && bv === '') return 0;
      if (av === '') return 1;
      if (bv === '') return -1;
      const cmp = av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return sorted;
  }, [combinedDocs, search, sourceFilter, dateFrom, dateTo, sortBy, sortDir]);

  function toggleSort(key: SortKey) {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir(key === 'title' || key === 'source' ? 'asc' : 'desc');
    }
  }

  function canOpenDoc(doc: CombinedDoc): boolean {
    return Boolean((doc.downloadSource && doc.downloadId) || doc.attachment || doc.access);
  }

  // Open a document from a row click: Quest/Vibrant reports and PDF attachments
  // preview inline in the right sidebar; non-PDF attachments download directly.
  function openDoc(doc: CombinedDoc) {
    if (doc.access) {
      onPreview({ kind: 'access-detail', labResultId: doc.access.labResultId, title: doc.access.previewTitle });
      return;
    }
    if (doc.downloadSource && doc.downloadId) {
      onPreview({ kind: 'report', source: doc.downloadSource, id: doc.downloadId, title: doc.title });
      return;
    }
    if (doc.attachment) {
      if (isPdfFilename(doc.attachment.filename)) {
        onPreview({ kind: 'attachment', patientId, attachmentId: doc.attachment.attachmentId, title: doc.attachment.filename || doc.title });
      } else {
        void downloadNoteAttachment(patientId, doc.attachment.attachmentId, doc.attachment.filename);
      }
    }
  }

  /**
   * Undo a soft delete. Biomarkers are invalidated alongside the list because
   * the biomarker query filters on notes.active too, so restoring the note
   * brings its biomarkers back with it.
   */
  async function handleRestore(noteId: number) {
    await restoreLabDocument(patientId, noteId);
    void queryClient.invalidateQueries({ queryKey: [documentsQueryKey, patientId] });
    void queryClient.invalidateQueries({ queryKey: ['patient-biomarkers', patientId] });
  }

  const isLoading = resultsLoading || docsLoading;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search documents..."
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm w-48 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="">All Sources</option>
          {sources.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <DatePicker size="sm" ariaLabel="From date" value={dateFrom} onChange={setDateFrom} wrapperClassName="w-36" />
        <span className="text-xs text-slate-400">to</span>
        <DatePicker size="sm" ariaLabel="To date" value={dateTo} onChange={setDateTo} wrapperClassName="w-36" />
        {(search || sourceFilter || dateFrom || dateTo) && (
          <Button size="sm" variant="ghost" onClick={() => { setSearch(''); setSourceFilter(''); setDateFrom(''); setDateTo(''); }}>
            Clear
          </Button>
        )}
        {/* Deliberately outside the Clear button's reset above: this is a
            visibility mode, not one of the four filters. */}
        {canShowDeleted && (
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none shrink-0 whitespace-nowrap">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
              className="rounded border-slate-300"
            />
            Show deleted
          </label>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : filtered.length === 0 ? (
        <p className="py-4 text-sm text-slate-400 italic">{emptyMessage}</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <SortableHeader label="Collect Date" sortKey="collectDate" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                <SortableHeader label="Result Date" sortKey="date" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                <SortableHeader label="Title" sortKey="title" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className="w-[40%]" />
                <SortableHeader label="Source" sortKey="source" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                {(canDeleteLabs || showDebug) && <th className="px-4 py-3 w-[60px] whitespace-nowrap" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((doc) => {
                const openable = canOpenDoc(doc);
                return (
                  <tr
                    key={doc.key}
                    className={`align-top ${openable ? 'cursor-pointer hover:bg-slate-50' : 'hover:bg-slate-50'} ${doc.deleted ? 'opacity-50' : ''}`}
                    onClick={openable ? () => openDoc(doc) : undefined}
                  >
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{formatDate(doc.collectDate)}</td>
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{formatDate(doc.date)}</td>
                    <td className="px-4 py-3 text-slate-800 [overflow-wrap:anywhere] min-w-0">
                      {doc.title}
                      {doc.access && (
                        <span className="ml-2 align-middle">
                          <AccessResultStatusBadge status={doc.access.status} label={doc.access.statusLabel} />
                        </span>
                      )}
                      {doc.deleted && (
                        <span className="ml-1 text-xs text-red-500">
                          {deletedLabel(doc)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-500 [overflow-wrap:anywhere] min-w-0">{doc.source}</td>
                    {(canDeleteLabs || showDebug) && (
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1">
                          {showDebug && doc.noteId != null && doc.attachment && (
                            <Button
                              size="xs"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                void runDebug(patientId, doc.noteId!, doc.attachment!.attachmentId, doc.attachment!.filename);
                              }}
                            >
                              Debug
                            </Button>
                          )}
                          {canDeleteLabs && doc.noteId != null && (
                            doc.deleted ? (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); void handleRestore(doc.noteId!); }}
                                className="text-slate-400 hover:text-green-600 transition-colors"
                                title="Restore"
                              >
                                <RotateCcw className="h-4 w-4" />
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setDeleteTarget(doc); }}
                                className="text-slate-400 hover:text-red-500 transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          if (!deleteTarget?.noteId) return;
          await deleteLabDocument(patientId, deleteTarget.noteId);
          void queryClient.invalidateQueries({ queryKey: [documentsQueryKey, patientId] });
          void queryClient.invalidateQueries({ queryKey: ['patient-biomarkers', patientId] });
        }}
        title="Delete Lab Document"
        message={`Are you sure you want to delete "${deleteTarget?.title ?? 'this document'}"? This action can be undone by an administrator.`}
        confirmLabel="Delete"
        variant="danger"
      />

      {(debugLoading || debugResult || debugError) && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => { setDebugResult(null); setDebugError(null); setDebugTitle(null); }}
        >
          <div
            className="max-h-[85vh] w-full max-w-3xl overflow-auto rounded-xl bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800">
                v4 download diagnostics{debugTitle ? ` — ${debugTitle}` : ''}
              </h3>
              <button
                type="button"
                onClick={() => { setDebugResult(null); setDebugError(null); setDebugTitle(null); }}
                className="text-slate-400 hover:text-slate-700"
              >
                ✕
              </button>
            </div>

            {debugLoading ? (
              <div className="flex justify-center py-8"><Spinner /></div>
            ) : debugError ? (
              <p className="text-sm text-red-600">{debugError}</p>
            ) : debugResult ? (
              <div className="space-y-4">
                {(['patient', 'staff'] as const).map((key) => {
                  const call = debugResult[key];
                  const ok = call.status >= 200 && call.status < 300;
                  return (
                    <div key={key} className="rounded-lg border border-slate-200">
                      <div className="flex items-center justify-between gap-2 border-b border-slate-100 bg-slate-50 px-3 py-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                          {key === 'patient' ? 'Patient route' : 'Staff route'}
                        </span>
                        <span className={`rounded px-2 py-0.5 text-xs font-medium ${ok ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          HTTP {call.status}
                        </span>
                      </div>
                      <div className="px-3 py-2">
                        <p className="mb-1 break-all font-mono text-[11px] text-slate-400">{call.url}</p>
                        <pre className="max-h-60 overflow-auto whitespace-pre-wrap break-all rounded bg-slate-900 p-2 font-mono text-[11px] text-slate-100">
{call.body || '(empty response)'}
                        </pre>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

function SortableHeader({
  label,
  sortKey,
  sortBy,
  sortDir,
  onSort,
  className,
}: {
  label: string;
  sortKey: SortKey;
  sortBy: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
  className?: string;
}) {
  const active = sortBy === sortKey;
  return (
    <th className={`px-4 py-3 ${className ?? ''}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`group inline-flex items-center gap-1 uppercase tracking-wide transition-colors ${
          active ? 'text-slate-700' : 'text-slate-500 hover:text-slate-700'
        }`}
      >
        {label}
        {active ? (
          sortDir === 'asc' ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />
        ) : (
          <ChevronsUpDown className="h-3.5 w-3.5 text-slate-300 group-hover:text-slate-400" />
        )}
      </button>
    </th>
  );
}
